import pdfMake from 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';
import moment from 'moment';
import {
  formatPhoneNumber,
  formatCurrency,
  formatDateDisplayLocal,
  formatDateTimeDisplayLocal,
  formatDateDisplayUTC,
  formatDateTimeDisplayUTC,
} from '@fortress-technology-solutions/fortress-component-library/utils';
import { formatTextingCounts } from './texting';

const browserPdfMake =
  typeof window !== 'undefined' ? window.pdfMake : undefined;
pdfMake.vfs = browserPdfMake?.vfs ?? pdfMake.vfs;

export const ContentVariants = Object.freeze({
  boolean: 'boolean',
  button: 'button',
  complianceApprovalStatus: 'complianceApprovalStatus',
  currency: 'currency',
  date: 'date',
  dateTime: 'dateTime',
  dateUTC: 'dateUTC',
  dateTimeUTC: 'dateTimeUTC',
  description: 'description',
  div: 'div',
  email: 'email',
  icon: 'icon',
  iconButton: 'iconButton',
  iconButtons: 'iconButtons',
  link: 'link',
  links: 'links',
  menu: 'menu',
  number: 'number',
  onOff: 'onOff',
  phoneNumber: 'phoneNumber',
  priority: 'priority',
  select: 'select',
  status: 'status',
  switch: 'switch',
  texting: 'texting',
});

const dateVariants = [
  ContentVariants.date,
  ContentVariants.dateTime,
  ContentVariants.dateTimeUTC,
  ContentVariants.dateUTC,
];

export const generateFooter = (
  currentPage: any,
  pageCount: any,
  { title, subtitle }: any,
): any => {
  const footerStack = [];
  if (title) {
    footerStack.push(title);
  }
  if (subtitle) {
    footerStack.push(subtitle);
  }
  return {
    columns: [
      {
        stack: [
          ...footerStack,
          {
            text: `Page ${currentPage.toString()} of ${pageCount}`,
            style: 'secondary',
          },
        ],
      },
    ],
    style: 'footer',
  };
};

const parseRowsForPdf = (rows, headers) => {
  return rows.map((row) => {
    const parsedRow = {};

    headers.forEach((header) => {
      const headerRow = row[header.id];
      const variant = headerRow?.variant;
      const value = headerRow?.value;

      let valueRow = value;

      // Boolean formatting
      if (typeof value === 'boolean') {
        valueRow = value ? 'Yes' : '';
      }

      // Texting object formatting
      else if (variant === ContentVariants.texting) {
        if (value) {
          valueRow = formatTextingCounts(value);
        }
      }

      // Array formatting (except iconButtons)
      else if (
        Array.isArray(value) &&
        variant !== ContentVariants.iconButtons
      ) {
        valueRow = `${value.join(', ')}`;
      }

      // Currency
      else if (variant === ContentVariants.currency) {
        valueRow = `${formatCurrency(value)}`;
      }

      // Dates
      else if (variant === ContentVariants.date) {
        valueRow = formatDateDisplayLocal(value);
      } else if (variant === ContentVariants.dateUTC) {
        valueRow = formatDateDisplayUTC(value);
      } else if (variant === ContentVariants.dateTime) {
        valueRow = formatDateTimeDisplayLocal(value);
      } else if (variant === ContentVariants.dateTimeUTC) {
        valueRow = formatDateTimeDisplayUTC(value);
      }

      // Phone
      else if (variant === ContentVariants.phoneNumber) {
        valueRow = formatPhoneNumber(value);
      }

      // String with comma or looking like a date
      else if (typeof value === 'string') {
        if (!dateVariants.includes(variant) && value.includes(',')) {
          valueRow = `${value.trimEnd()}`;
        } else if (
          !dateVariants.includes(variant) &&
          /^\d+\/\d+$/.test(value)
        ) {
          valueRow = `=${value}`;
        }
      }

      // Empty / falsy values
      const isEmpty =
        value == null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0);
      if (isEmpty) {
        valueRow = '';
      }

      // Final assignment
      parsedRow[header.id] = {
        variant: variant ?? '',
        value: valueRow,
      };
    });

    return parsedRow;
  });
};

const forceWrap = (str, maxChunk = 30) => {
  if (typeof str !== 'string') return str;

  return str.replace(new RegExp(`(\\S{${maxChunk}})`, 'g'), '$1\u200B'); // \u200B = zero-width space
};

export const exportAndDownloadPDF = ({
  excludedHeaders = [],
  filteredHeaders = [],
  fileName = 'documento',
  hasAnyFilters = false,
  rows = [],
  title,
  subtitle,
}: {
  excludedHeaders: string[],
  filteredHeaders: {
    id: string,
    label: string | { props: { defaultMessage: string } },
  }[],
  fileName: string,
  hasAnyFilters: boolean,
  rows: { [headerId: string]: { variant: string, value: string | number } }[],
}) => {
  const visibleHeaders = filteredHeaders.filter(
    (h) => !excludedHeaders.includes(h.id),
  );
  const headerLabels = visibleHeaders.map((h) =>
    typeof h.label === 'string' ? h.label : h.label.props.defaultMessage,
  );

  const styledHeaderLabels = headerLabels.map((label) => ({
    text: label,
    fontSize: 6,
    bold: true,
    style: 'tableHeader',
  }));

  const parsedRows = parseRowsForPdf(rows, visibleHeaders);
  const styledTableRows = parsedRows.map((row) =>
    visibleHeaders.map((h) => ({
      text: forceWrap(row[h.id]?.value ?? ''),
      fontSize: 6,
    })),
  );

  const margin = [0, 0, 0, 40];

  const docDefinition = {
    pageSize: 'LEGAL',
    pageOrientation: 'landscape',
    header: {
      columns: [
        {
          stack: [
            title,
            subtitle,
            {
              text: `Date: ${moment().format('MM-DD-YYYY')}`,
              style: 'secondary',
            },
          ],
        },
      ],
      style: 'header',
    },
    content: [
      {
        style: 'table',
        table: {
          headerRows: 1,
          widths: visibleHeaders.map(() => 'auto'),
          body: [styledHeaderLabels, ...styledTableRows],
        },

        layout: {
          hLineColor: [142, 168, 217],
          vLineColor: [142, 168, 217],
          hLineWidth: () => 0.1,
          vLineWidth: () => 0.1,
          paddingLeft: () => 2,
          paddingRight: () => 2,
          paddingTop: () => 2,
          paddingBottom: () => 1,
        },
        margin,
      },
    ],
    footer: (currentPage, pageCount) =>
      generateFooter(currentPage, pageCount, { title, subtitle }),
    styles: {
      filteredText: {
        alignment: 'left',
        color: '#4E4E4E',
        margin: [2, 2, 2, 2],
      },
      footer: {
        fontSize: 10,
        lineHeight: '0.9',
      },
      header: {
        fontSize: 12,
        lineHeight: '0.9',
        margin: [0, 16, 0, 0],
      },
      secondary: {
        fontSize: 8,
        margin: [0, 0, 0, 0],
      },
      table: {
        color: '#4E4E4E',
        fontSize: 6,
        overflow: 'linebreak',
      },
      tableCell: {
        margin: [0, 2, 0, 2],
      },
      tableHeader: {
        bold: true,
        color: '#fff',
        fillColor: '#4D73FF',
        margin: [0, 8, 0, 8],
      },
      tableTitle: {
        fontSize: 10,
        bold: true,
        alignment: 'left',
        margin: [0, 0, 0, 10],
      },
      tableCellFillColor: {
        margin: [0, 0, 0, 0],
        fillColor: '#d9e1f1',
      },
    },
    defaultStyle: {
      alignment: 'center',
    },
  };

  pdfMake.createPdf(docDefinition).download(`${fileName}`);
};
